// ISDP CUSTOM: Arrow-key navigation for the search modal. Extracted verbatim
// from SearchModal.
//
// Tracks the 0-based index of the highlighted row (-1 = none). The nav spans BOTH
// the command rows (rendered first) and the live search results (rendered after);
// the shared selector preserves DOM order so index math stays correct. Commands
// (.devhub-cmdk__item) precede the shared result rows (.devhub-sr__item) in the
// DOM, so querySelectorAll returns them in visual order.
//
// The caller attaches the returned `panelRef` to the modal panel root and wires
// `handleKeyNav` to its onKeyDown. Passing term/kindFilter/scoped lets the hook
// reset the highlight whenever the result/command set changes — identical to the
// original inline effect.
import { useCallback, useEffect, useRef, useState } from 'react';

const NAV_SELECTOR =
  '.devhub-cmdk__item, .devhub-search-modal__results .devhub-sr__item';

export function useResultKeyboardNav(deps: {
  term: string;
  kindFilter: string;
  scoped: boolean;
}) {
  const { term, kindFilter, scoped } = deps;
  const [activeIndex, setActiveIndex] = useState(-1);
  // Panel root — the nav query runs against this so it sees command rows AND
  // result rows.
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset the active index whenever the result/command set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [term, kindFilter, scoped]);

  // Sync the `is-active` CSS class to the highlighted row after each render.
  // We do this via a DOM effect (not controlled rendering) because the result
  // rows are emitted by Backstage's SearchResultListItemExtensions.
  useEffect(() => {
    if (!panelRef.current) return;
    const items = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(NAV_SELECTOR),
    );
    items.forEach((item, i) => {
      item.classList.toggle('is-active', i === activeIndex);
    });
  });

  // Keyboard handler — ArrowDown/Up moves the highlight; Enter activates.
  const handleKeyNav = useCallback(
    (e: React.KeyboardEvent) => {
      if (!panelRef.current) return;
      const items = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(NAV_SELECTOR),
      );
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => {
          const next = prev < items.length - 1 ? prev + 1 : 0; // wrap-around
          items[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => {
          const next = prev > 0 ? prev - 1 : items.length - 1; // wrap-around
          items[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        // Activate the highlighted item — find its first anchor and click it.
        const item = items[activeIndex];
        if (item) {
          const anchor = item.querySelector<HTMLElement>('a, [role="link"], [tabindex="0"]');
          if (anchor) {
            anchor.click();
          } else {
            item.click();
          }
        }
      }
    },
    [activeIndex],
  );

  return { panelRef, handleKeyNav };
}
